import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isLoggingOut: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Don't update state if we're logging out - prevents flashes
        if (event === 'SIGNED_OUT' && isLoggingOut) {
          return;
        }
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Check for existing session with error handling
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      })
      .catch(() => {
        // Network error or Supabase unreachable - continue without auth
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [isLoggingOut]);

  const signOut = useCallback(async () => {
    setIsLoggingOut(true);
    
    try {
      // Clear localStorage first
      Object.keys(window.localStorage)
        .filter((key) => key.startsWith('sb-'))
        .forEach((key) => window.localStorage.removeItem(key));

      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear state
      setUser(null);
      setSession(null);
      
      // Navigate to home page smoothly (no full reload)
      window.location.replace('/');
      
    } catch (error) {
      console.error('Sign out error:', error);
      // Still clear state and redirect even if Supabase call fails
      setUser(null);
      setSession(null);
      window.location.replace('/');
    }
  }, []);

  const value = {
    user,
    session,
    loading,
    isLoggingOut,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
